import { useDataModel } from '@thd-nucleus/data-sources';
import React, { createContext, useMemo, useState, useEffect } from 'react';
import { addresses } from '../models/graphQLModels';
import { IAddress, IPhone } from '../models/address';
import { getChannelID } from '../utils/general';
import { useInstantCheckoutStatus } from '../hooks/use-instant-checkout-status';
import { TAlertStatus, TAlertType } from '../models/common/alert';
import {
  ERR_GENERIC,
  ERR_REDIRECT,
} from '../constants/response-error-messages';
import { getIsAuthError } from '../constants/error-codes';
import { createNewRelicActions, logNewRelicAction } from '../utils/newrelic';

export interface IAddressContextValues {
  addresses: IAddress[];
  hasAddresses: () => boolean;
  selectedAddress: IAddress | null;
  setSelectedAddress: React.Dispatch<React.SetStateAction<IAddress | null>>;
  refetchAddresses: () => void;
  addressLoading: boolean;
  defaultAddress: IAddress | undefined;
}

export const AddressContext = createContext<IAddressContextValues | null>(null);

export const AddressContextProvider = ({
  children,
  userId,
  customerAccountId,
}: {
  children: React.ReactNode;
  userId: string;
  customerAccountId: string;
}) => {
  const channelId = getChannelID();
  const actions = createNewRelicActions('AddressContextProvider');

  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>(null);

  const { setAlertMessage } = useInstantCheckoutStatus();

  const hasAddresses = (): boolean => {
    return addressList?.length > 0;
  };

  const { data, refetch, loading, error } = useDataModel('addresses', {
    variables: {
      userId,
      customerAccountId,
      channelId,
      paginate: { pageNumber: '0', pageSize: '0' },
    },
    skip: !userId || !customerAccountId,
    ssr: false,
    context: { withAuth: true },
    notifyOnNetworkStatusChange: true,
  });

  const addressList: IAddress[] = useMemo(() => {
    return data?.addresses?.addresses?.map((address: IAddress): IAddress => {
      return {
        id: address?.id,
        isDefault: address?.isDefault,
        isInstantCheckoutDefault: address?.isInstantCheckoutDefault,
        nickName: address?.nickName,
        lastModifiedDate: address?.lastModifiedDate,
        name: {
          firstName: address?.name?.firstName,
          lastName: address?.name?.lastName,
        },
        phones: address?.phones?.map((phone): IPhone => {
          return {
            number: phone?.number,
            id: phone?.id,
            isPrimary: phone?.isPrimary ?? false,
          };
        }),
        postalAddress: {
          addressLine1: address?.postalAddress?.addressLine1,
          addressLine2: address?.postalAddress?.addressLine2,
          city: address?.postalAddress?.city,
          country: address?.postalAddress?.country,
          stateProvince: address?.postalAddress?.stateProvince,
          zipCode: address?.postalAddress?.zipCode,
        },
      };
    });
  }, [data]);

  useEffect(() => {
    if (error) {
      logNewRelicAction(actions.ADDRESSES, error);
      if (getIsAuthError(error)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
      } else {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.ERROR,
          text: ERR_GENERIC.message,
        });
      }
    } else if (data) {
      logNewRelicAction(actions.ADDRESSES);
    }
  }, [actions.ADDRESSES, data, error, setAlertMessage]);

  return (
    <AddressContext.Provider
      value={{
        addresses: addressList || [],
        hasAddresses,
        selectedAddress,
        setSelectedAddress,
        refetchAddresses: refetch,
        addressLoading: loading,
        defaultAddress: addressList?.find(
          (address) => address.isInstantCheckoutDefault,
        ),
      }}
    >
      {children}
    </AddressContext.Provider>
  );
};

AddressContextProvider.dataModel = {
  addresses,
};
